import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../db";
import {
  patients as patientsTable,
  cases as casesTable,
  units as unitsTable,
} from "../../../shared/src/schema";
import { useStore, useCommand, useRpc, useListVersion } from "../store";
import { useI18n } from "../i18n";
import { uuidv7 } from "../../../shared/src/ids";
import { useStyles, useColors } from "../ui";
import { useAnon } from "../anon";
import { Pagination } from "../components/Pagination";
import type {
  PatientListItem,
  PatientListResult,
} from "../../../shared/src/protocol";

const PAGE_SIZE = 50;
let _hasData = false;

interface ColDef {
  key: string;
  labelKey: string;
  sortKey?: string; // server sort key; omit = not sortable
  minWidth?: string;
}

const COLUMNS: ColDef[] = [
  { key: "name", labelKey: "patient.name", sortKey: "name" },
  {
    key: "dob",
    labelKey: "patient.dob",
    sortKey: "dateOfBirth",
    minWidth: "100px",
  },
  { key: "sex", labelKey: "patient.sex", sortKey: "sex" },
  { key: "blood", labelKey: "patient.bloodType", sortKey: "bloodType" },
  { key: "cases", labelKey: "patient.cases", sortKey: "caseCount" },
  { key: "rank", labelKey: "patient.rank", sortKey: "rank" },
  { key: "unit", labelKey: "patient.unit", sortKey: "unit" },
];

export function PatientsPage() {
  const { t } = useI18n();
  const dispatch = useCommand();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rpc = useRpc();
  const { online } = useStore();
  const listVersion = useListVersion();
  const styles = useStyles();
  const colors = useColors();
  const { anonName, anonUnit, anonDob } = useAnon();

  const currentPage = Math.max(
    1,
    parseInt(searchParams.get("page") || "1", 10) || 1,
  );

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Sort state
  const [sortKey, setSortKey] = useState("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [showArchived, setShowArchived] = useState(false);

  // Server data
  const [patients, setPatients] = useState<PatientListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const fetchIdRef = useRef(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const setPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (page <= 1) next.delete("page");
          else next.set("page", String(page));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    if (currentPage !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const toggleSort = (key: string | undefined) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // Fetch patients from server (online) or local DB (offline)
  const fetchPatients = useCallback(async () => {
    const id = ++fetchIdRef.current;
    if (!_hasData) setLoading(true);
    try {
      let result: PatientListResult;
      if (online) {
        result = await rpc<PatientListResult>({
          type: "listPatients",
          search: debouncedSearch || undefined,
          sortKey,
          sortDir,
          page: currentPage,
          limit: PAGE_SIZE,
          includeArchived: showArchived || undefined,
        });
      } else {
        // Offline: query locally synced data
        const [allPatients, allCases, allUnits] = await Promise.all([
          db.select().from(patientsTable),
          db.select({ patientId: casesTable.patientId }).from(casesTable),
          db.select().from(unitsTable),
        ]);
        const uMap = new Map(allUnits.map((u) => [u.id, u]));
        // Count cases per patient
        const caseCountMap = new Map<string, number>();
        for (const c of allCases) {
          caseCountMap.set(
            c.patientId,
            (caseCountMap.get(c.patientId) ?? 0) + 1,
          );
        }

        const source = showArchived ? allPatients : allPatients.filter((p) => !p.archived);
        let items: PatientListItem[] = source.map((p) => ({
          id: p.id,
          name: p.name,
          dateOfBirth: p.dateOfBirth,
          sex: p.sex,
          bloodType: p.bloodType,
          rhFactor: p.rhFactor,
          allergyStatus: p.allergyStatus,
          allergyDetails: p.allergyDetails,
          militaryRank: p.militaryRank,
          unitId: p.unitId,
          unitName: p.unitId ? (uMap.get(p.unitId)?.name ?? "") : "",
          caseCount: caseCountMap.get(p.id) ?? 0,
          updatedAt: p.updatedAt,
        }));

        // Search filter
        if (debouncedSearch) {
          const q = debouncedSearch.toLowerCase();
          items = items.filter(
            (p) =>
              p.name?.toLowerCase().includes(q) ||
              p.militaryRank?.toLowerCase().includes(q) ||
              p.unitName?.toLowerCase().includes(q),
          );
        }

        // Sort
        const skMap: Record<string, keyof PatientListItem> = {
          name: "name",
          dateOfBirth: "dateOfBirth",
          sex: "sex",
          bloodType: "bloodType",
          rank: "militaryRank",
          unit: "unitName",
          caseCount: "caseCount",
          updatedAt: "updatedAt",
        };
        const field = skMap[sortKey] ?? "updatedAt";
        const dir = sortDir === "asc" ? 1 : -1;
        items.sort((a, b) => {
          const av = a[field],
            bv = b[field];
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          if (typeof av === "string")
            return dir * av.localeCompare(bv as string);
          if (typeof av === "number")
            return dir * ((av as number) - (bv as number));
          return 0;
        });

        // Paginate
        const total = items.length;
        const start = (currentPage - 1) * PAGE_SIZE;
        result = {
          patients: items.slice(start, start + PAGE_SIZE),
          page: currentPage,
          totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
          totalCount: total,
        };
      }
      if (id !== fetchIdRef.current) return; // stale
      setPatients(result.patients);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      _hasData = true;
    } catch (e) {
      console.error("Patients fetch error:", e);
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [online, rpc, debouncedSearch, sortKey, sortDir, currentPage, showArchived]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchPatients();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchPatients]);

  // Re-fetch when server broadcasts a list invalidation
  useEffect(() => {
    if (listVersion > 0) fetchPatients();
  }, [listVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPatient = async () => {
    const uuid = uuidv7();
    const { ack } = await dispatch("patient.create", { uuid, unitUuid: "" });
    await ack;
    navigate(`/patients/${uuid}`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ ...styles.toolbar, flexShrink: 0 }}>
        <input
          placeholder={t("cases.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, maxWidth: "240px" }}
        />
        <span style={{ fontSize: "0.8rem", color: colors.textMuted }}>
          {totalCount > 0 && `${totalCount}`}
        </span>
        <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8rem", color: colors.textMuted, cursor: "pointer" }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          {t("common.showArchived")}
        </label>
        <span style={{ flex: 1 }} />
        <button onClick={createPatient} style={styles.btnPrimary}>
          {t("patient.new")}
        </button>
      </div>
      <div style={{ flex: 1, overflow: "auto", minWidth: 0 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...styles.th,
                    ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                    cursor: col.sortKey ? "pointer" : "default",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSort(col.sortKey)}
                >
                  {t(col.labelKey)}
                  {col.sortKey && sortKey === col.sortKey && (
                    <span style={{ marginLeft: "4px" }}>
                      {sortDir === "asc" ? "\u25B2" : "\u25BC"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {patients.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    ...styles.td,
                    color: colors.textMuted,
                    textAlign: "center",
                  }}
                >
                  {t("cases.noResults")}
                </td>
              </tr>
            )}
            {patients.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/patients/${p.id}`)}
                style={{ cursor: "pointer" }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = colors.bgAlt)
                }
                onMouseOut={(e) => (e.currentTarget.style.background = "")}
              >
                <td style={styles.td}>{anonName(p.name) || "\u2014"}</td>
                <td style={styles.td}>{anonDob(p.dateOfBirth) || ""}</td>
                <td style={styles.td}>{p.sex ? t(`sex.${p.sex}`) : ""}</td>
                <td style={styles.td}>
                  {[
                    p.bloodType,
                    p.rhFactor === "positive"
                      ? "+"
                      : p.rhFactor === "negative"
                        ? "-"
                        : "",
                  ]
                    .filter(Boolean)
                    .join("")}
                </td>
                <td style={styles.td}>{p.caseCount || ""}</td>
                <td style={styles.td}>{p.militaryRank || ""}</td>
                <td style={styles.td}>{anonUnit(p.unitId, p.unitName)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: colors.textMuted,
            }}
          >
            {t("app.loading")}
          </div>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
